import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { DirectMessage, Conversation, getConversations, getDirectMessages, sendDirectMessage, subscribeToDirectMessages, markMessagesAsRead } from '../../lib/chat';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Message } from './Message';
import { RealtimeChannel } from '@supabase/supabase-js';

export function DirectMessages() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  // Track which conversations have been read
  const [readConversations, setReadConversations] = useState<Set<string>>(new Set());

  // Load conversations initially and update unread counts
  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (username) {
      const conversation = conversations.find(c => c.username.toLowerCase() === username.toLowerCase());
      if (!conversation || !conversation.user_id) {
        setError(`Keine Konversation mit ${username} gefunden`);
        return;
      }
      
      setSelectedConversation(conversation);
      loadMessages(conversation.user_id);

      // Mark messages as read and update state
      const markAsRead = async () => {
        try {
          await markMessagesAsRead(conversation.user_id);
          // Add to read conversations set
          setReadConversations(prev => new Set(prev).add(conversation.user_id));
          // Update local unread counts
          setUnreadCounts(prev => ({ ...prev, [conversation.user_id]: 0 }));
          // Update conversations to reflect read state
          setConversations(prev => 
            prev.map(conv => 
              conv.user_id === conversation.user_id 
                ? { ...conv, unread_count: 0 }
                : conv
            )
          );
        } catch (error) {
          console.error('Error marking messages as read:', error);
        }
      };

      markAsRead();
      
      // Set up real-time subscription
      if (user) {
        subscriptionRef.current = subscribeToDirectMessages(user.id, async (message) => {
          if (message.sender_id === conversation.user_id) {
            // Add message to UI
            setMessages(prev => [...prev, message]);
            // Mark new message as read immediately since conversation is open
            await markMessagesAsRead(conversation.user_id);
          }
        });
      }
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [username, conversations, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await getConversations();
      setConversations(data);
      // Update unread counts
      const counts: Record<string, number> = {}; 
      data.forEach(conv => {
        // Only show unread count if conversation hasn't been read
        counts[conv.user_id] = readConversations.has(conv.user_id) ? 0 : conv.unread_count;
      });
      setUnreadCounts(counts);
      setLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setError('Fehler beim Laden der Konversationen');
      setLoading(false);
    }
  };

  const loadMessages = async (userId: string) => {
    try {
      const messages = await getDirectMessages(userId);
      
      // Mark messages as read immediately after loading
      if (messages.some(m => !m.read && m.sender_id === userId)) {
        await markMessagesAsRead(userId);
      }
      
      setMessages(messages);
    } catch (error) {
      console.error('Error loading messages:', error);
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Laden der Nachrichten';
      setError(errorMessage);
      setTimeout(() => setError(''), 3000); // Clear error after 3 seconds
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConversation || !newMessage.trim()) return;

    try {
      const message = await sendDirectMessage(selectedConversation.user_id, newMessage.trim());

      // Fetch sender profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, avatar_url, artist_types')
        .eq('id', message.sender_id)
        .single();
      
      if (!profile) {
        throw new Error('Could not fetch sender profile');
      }
      
      // Add profile to message
      const messageWithProfile = {
        ...message,
        profiles: profile
      };

      setMessages(prev => [...prev, messageWithProfile]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Senden der Nachricht');
      setTimeout(() => setError(''), 3000); // Clear error after 3 seconds
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-text-secondary">Laden...</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {error && (
        <div className="absolute top-4 right-4 bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-2 rounded-lg">
          {error}
        </div>
      )}

      {/* Conversations List */}
      <div className="w-64 border-r border-divider flex flex-col">
        <div className="p-4 border-b border-divider">
          <h2 className="text-lg font-bold">Nachrichten</h2>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.map((conversation) => (
            <button
              key={conversation.user_id}
              onClick={() => navigate(`/app/profile/messages/${conversation.username.toLowerCase()}`)}
              className={`w-full p-4 flex items-center gap-3 hover:bg-background-secondary transition-colors ${
                selectedConversation?.user_id === conversation.user_id ? 'bg-background-secondary' : ''
              }`}
            >
              <img
                src={conversation.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${conversation.username}`}
                alt={conversation.username}
                className="w-12 h-12 rounded-full"
              />
              <div className="flex-1 text-left">
                <div className="font-medium">{conversation.username}</div>
                {conversation.last_message && (
                  <p className={`
                    "text-xs truncate",
                    ${unreadCounts[conversation.user_id] > 0 ? "text-text-primary font-medium" : "text-text-secondary"}
                  `}>
                    {conversation.last_message}
                  </p>
                )}
              </div>
              {unreadCounts[conversation.user_id] > 0 && (
                <div className="bg-accent-primary text-white text-xs min-w-[1.25rem] h-5 flex items-center justify-center rounded-full font-medium">
                  {unreadCounts[conversation.user_id]}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-divider flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="md:hidden p-2 hover:bg-background-secondary rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <img
              src={selectedConversation.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${selectedConversation.username}`}
              alt={selectedConversation.username}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <div className="font-medium">{selectedConversation.username}</div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <Message
                key={message.id}
                id={message.id}
                user={{
                  id: message.sender_id,
                  name: message.profiles?.username || 'Unbekannter Benutzer',
                  image: message.profiles?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${message.profiles?.username}`,
                  role: message.profiles?.artist_types[0] || 'Künstler'
                }}
                content={message.content}
                timestamp={new Date(message.created_at).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-divider">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Nachricht schreiben..."
                className="flex-1 bg-background-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-accent-primary"
              />
              <button 
                type="submit"
                disabled={!newMessage.trim()}
                className="p-2 text-accent-primary hover:text-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-text-secondary">
          Wähle eine Konversation aus
        </div>
      )}
    </div>
  );
}